import { useCallback, useEffect, useRef, useState } from 'react';
import ArticleLink from '@/components/feature/ArticleLink';
import { learningPath } from '@/mocks/home';
import ScrollReveal from '@/components/base/ScrollReveal';
import SectionHeading from '@/components/base/SectionHeading';

const GRID_GAP = 24;
const NODE_SIZE = 48;
const COLUMN_COUNT = learningPath.length;

export default function LearningPath() {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;

    const measure = () => setTrackWidth(node.clientWidth);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const centerAt = useCallback(
    (index: number): number | null => {
      if (trackWidth <= 0) return null;
      const columnWidth = (trackWidth - GRID_GAP * (COLUMN_COUNT - 1)) / COLUMN_COUNT;
      return index * (columnWidth + GRID_GAP) + NODE_SIZE / 2;
    },
    [trackWidth],
  );

  const startX = centerAt(0);
  const activeX = activeStep !== null ? centerAt(activeStep) : null;
  const hasProgress = startX !== null && activeX !== null && activeX - startX > 1;

  return (
    <section className="border-y border-background-200 bg-background-100/60">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="เส้นทางการเรียนรู้"
          title="เส้นทางการเรียนรู้ Forex"
          subtitle="เรียงลำดับการเรียนรู้ให้มือใหม่เข้าใจง่าย ตั้งแต่ทำความรู้จักตลาดไปจนถึงการบริหารความเสี่ยง"
        />

        <div className="relative mt-12" onMouseLeave={() => setActiveStep(null)}>
          <div className="absolute left-6 top-2 h-[calc(100%-1rem)] w-px bg-background-300 md:hidden" />
          <div className="absolute left-0 right-0 top-6 hidden h-px bg-background-300 md:block" />
          {hasProgress && startX !== null && activeX !== null ? (
            <div
              className="pointer-events-none absolute top-6 z-[1] hidden h-[2px] -translate-y-px rounded-full bg-primary-500 transition-all duration-300 ease-out md:block"
              style={{ left: `${startX}px`, width: `${activeX - startX}px` }}
            />
          ) : null}
          <div ref={gridRef} className="grid grid-cols-1 gap-10 md:grid-cols-5 md:gap-6">
            {learningPath.map((step, index) => {
              const dimmed = activeStep !== null && activeStep !== index;
              return (
                <ScrollReveal key={step.no} delay={index * 90}>
                  <ArticleLink
                    to={step.path}
                    onMouseEnter={() => setActiveStep(index)}
                    onFocus={() => setActiveStep(index)}
                    onBlur={() => setActiveStep(null)}
                    className={`group flex gap-4 rounded-lg transition-opacity duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 md:block ${
                      dimmed ? 'opacity-40' : 'opacity-100'
                    }`}
                  >
                    <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-background-50 font-num text-lg font-semibold text-primary-700 transition-all duration-300 ease-out group-hover:scale-110 group-hover:border-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 group-hover:ring-4 group-hover:ring-primary-100">
                      {step.no}
                    </span>
                    <div className="md:mt-5">
                      <h3 className="flex items-center gap-1.5 font-heading text-base font-semibold text-foreground-950 transition-colors duration-300 group-hover:text-primary-700 md:text-lg">
                        {step.title}
                        <i className="ri-arrow-right-line -translate-x-1 text-base text-primary-600 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600 transition-colors duration-300 group-hover:text-foreground-700">
                        {step.desc}
                      </p>
                    </div>
                  </ArticleLink>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}